import { createContext, useContext, useEffect, useState } from 'react';

const MediaQueryContext = createContext<string>('lg');

export const viewPortSizeMap = {
  minMobile: 767,
  minTablet: 768,
  maxTablet: 1023,
  minDesktop: 1024,
};

export function getWindowSizeCategory(width: number) {
  const categoryMap: { [key: number]: string } = {
    [viewPortSizeMap.minMobile]: 'sm',
    [viewPortSizeMap.minTablet]: 'md',
    [viewPortSizeMap.maxTablet]: 'md',
    [viewPortSizeMap.minDesktop]: 'lg',
  };

  const maxWidth =
    Object.keys(categoryMap)
      .map(Number)
      .find((size) => width < size) || viewPortSizeMap.minDesktop;
  return categoryMap[maxWidth];
}

function getWindowSize() {
  if (typeof window !== 'undefined') {
    return window.document.body.clientWidth;
  }
  return null;
}

const MediaQueryProvider = ({
  children,
  deviceType,
}: {
  children: React.ReactNode;
  deviceType: string;
}) => {
  const [sizeCategory, setSizeCategory] = useState<string>(
    deviceType === 'mobile' ? 'sm' : 'lg'
  );

  useEffect(() => {
    const handleResize = () => {
      const width = getWindowSize();
      if (!width) return;

      setSizeCategory(getWindowSizeCategory(width));
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <MediaQueryContext.Provider value={sizeCategory}>
      {children}
    </MediaQueryContext.Provider>
  );
};

export { MediaQueryProvider, MediaQueryContext };

export const useMediaQuery = () => {
  const context = useContext(MediaQueryContext);
  if (!context) {
    throw new Error('useMediaQuery must be used within a MediaQueryProvider');
  }
  return context;
};
